import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera, Copy, Trash2, ExternalLink, Loader2,
  ArrowLeft, ShieldCheck, RefreshCw, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * AdminSnapshotsPage — WebAdmin / BCM Manager portal to manage prospect-shareable
 * read-only demo snapshots. Reachable at /admin/snapshots.
 *
 * - Generates a sanitized, time-boxed snapshot of the demo tenant.
 * - Lists all existing snapshots with view counts.
 * - Lets admins copy share URLs and revoke snapshots.
 */
export default function AdminSnapshotsPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [label, setLabel] = useState("");
  const [ttlDays, setTtlDays] = useState(14);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/demo-snapshot/list");
      setSnapshots(res.data?.snapshots || []);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to load snapshots"));
    } finally {
      setLoading(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/admin/demo-snapshot/generate", {
        label: label || undefined,
        ttl_days: Number(ttlDays) || 14,
      });
      toast.success("Snapshot generated successfully");
      setLabel("");
      // Auto-copy the share URL
      const shareUrl = `${window.location.origin}${res.data.share_url_path}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Share link copied to clipboard");
      } catch { /* clipboard may be blocked in iframe */ }
      await fetchAll();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to generate snapshot"));
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = async (token) => {
    const url = `${window.location.origin}/snapshot/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied");
    } catch {
      toast.error("Could not copy — please copy manually");
    }
  };

  const revoke = async (id, label) => {
    if (!window.confirm(`Revoke snapshot "${label}"? Anyone with the link will see "snapshot not found".`)) return;
    try {
      await api.delete(`/admin/demo-snapshot/${id}`);
      toast.success("Snapshot revoked");
      await fetchAll();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to revoke"));
    }
  };

  useEffect(() => { fetchAll(); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50 p-4 sm:p-8" data-testid="admin-snapshots-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/admin")}
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1 mb-3"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Admin Console
          </button>
          <div className="flex items-center gap-3">
            <Camera className="w-7 h-7 text-sky-600" />
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Demo Snapshots</h1>
              <p className="text-sm text-slate-600">
                Prospect-shareable, read-only, time-boxed snapshots of the demo tenant.
              </p>
            </div>
          </div>
        </div>

        {/* Generate panel */}
        <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4 text-emerald-600" />
            <h2 className="font-semibold text-slate-800">Generate new snapshot</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-600 block mb-1">Label (optional)</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Acme Corp · CISO Evaluation"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="snapshot-label-input"
              />
            </div>
            <div>
              <label className="text-xs text-slate-600 block mb-1">Expires in (days)</label>
              <input
                type="number"
                min="1"
                max="90"
                value={ttlDays}
                onChange={(e) => setTtlDays(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="snapshot-ttl-input"
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={generate}
              disabled={generating}
              className="bg-sky-600 hover:bg-sky-700 text-white font-medium px-4 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-60"
              data-testid="generate-snapshot-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              {generating ? "Generating…" : "Generate snapshot"}
            </button>
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> PII auto-pseudonymized · Read-only · No login required
            </p>
          </div>
        </div>

        {/* List */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <h2 className="font-semibold text-slate-800">Active snapshots</h2>
            <button
              onClick={fetchAll}
              className="text-xs text-slate-600 hover:text-slate-900 flex items-center gap-1"
              data-testid="refresh-snapshots-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-sky-600 mx-auto" /></div>
          ) : snapshots.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 text-center">No snapshots yet. Generate one above to get started.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {snapshots.map((s) => {
                const expired = s.expires_at && new Date(s.expires_at) < new Date();
                const url = `${window.location.origin}/snapshot/${s.token}`;
                return (
                  <li key={s.id} className="px-5 py-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`snapshot-row-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-slate-800 truncate">{s.label || "Untitled snapshot"}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Industry: <span className="capitalize">{s.industry || "—"}</span>
                        {" · "}Created {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                        {" · "}{expired ? <span className="text-rose-600">Expired {new Date(s.expires_at).toLocaleDateString()}</span> : <>Expires {new Date(s.expires_at).toLocaleDateString()}</>}
                        {" · "}{s.view_count || 0} view{(s.view_count || 0) === 1 ? "" : "s"}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyLink(s.token)}
                        className="text-xs px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md text-slate-700 flex items-center gap-1"
                        data-testid={`copy-snapshot-link-${s.id}`}
                      >
                        <Copy className="w-3 h-3" /> Copy link
                      </button>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs px-2.5 py-1.5 bg-sky-50 hover:bg-sky-100 rounded-md text-sky-700 flex items-center gap-1"
                        data-testid={`view-snapshot-${s.id}`}
                      >
                        <ExternalLink className="w-3 h-3" /> View
                      </a>
                      <button
                        onClick={() => revoke(s.id, s.label || "Untitled")}
                        className="text-xs px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 rounded-md text-rose-700 flex items-center gap-1"
                        data-testid={`revoke-snapshot-${s.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Revoke
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Help */}
        <div className="mt-6 bg-sky-50 border border-sky-200 rounded-lg p-4 text-xs text-slate-700 leading-relaxed">
          <p className="font-semibold text-sky-800 mb-1">How to use snapshots</p>
          <ol className="list-decimal pl-4 space-y-1">
            <li>Generate a snapshot of the live demo tenant — PII is auto-pseudonymized.</li>
            <li>Send the share link to a prospect's evaluator (CISO, BCM lead, etc.).</li>
            <li>They view a read-only Mission Control without creating an account.</li>
            <li>You can revoke at any time. Snapshots auto-expire after the TTL.</li>
          </ol>
        </div>
      </div>
    </div>
  );
}
